import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [billingCycles, setBillingCycles] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    organizationId: '',
    offeringId: '',
    billingCycleId: '',
    currencyId: '',
    erpCode: '',
    paymentsCurrents: false,
    status: 'incomplete',
    readOnlyAccess: false,
    startDate: '',
    endDate: '',
    postSubscriptionReadOnlyDays: 0,
    billingCycleCharge: '',
    setupCharge: '',
    totalContractValue: '',
    maxUsers: '',
    enforcedUserLimit: false,
    autoRenew: true
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [currentPage, itemsPerPage]);

  const loadInitialData = async () => {
    try {
      const [orgResponse, offeringsResponse, billingCyclesResponse, currenciesResponse] = await Promise.all([
        apiService.getOrganizations(1, 100),
        apiService.getOfferings(1, 100),
        apiService.getBillingCycles(),
        apiService.getCurrencies()
      ]);

      if (orgResponse.success) {
        setOrganizations(orgResponse.data || []);
      }
      if (offeringsResponse.success) {
        setOfferings(offeringsResponse.data?.offerings || offeringsResponse.data || []);
      }
      if (billingCyclesResponse.success) {
        setBillingCycles(billingCyclesResponse.data || []);
      }
      if (currenciesResponse.success) {
        setCurrencies(currenciesResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadSubscriptions = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiService.getSubscriptions(currentPage, itemsPerPage);
      if (response.success) {
        setSubscriptions(response.data || []);
        setTotalPages(Math.ceil((response.data?.total || 0) / itemsPerPage));
      } else {
        setError(response.error || 'Failed to load subscriptions');
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (subscription = null) => {
    if (subscription) {
      setEditingSubscription(subscription);
      setFormData({
        organizationId: subscription.organization_id || '',
        offeringId: subscription.offering_id || '',
        billingCycleId: subscription.billing_cycle_id || '',
        currencyId: subscription.currency_id || '',
        erpCode: subscription.erp_code || '',
        paymentsCurrents: subscription.payments_current || false,
        status: subscription.status || 'incomplete',
        readOnlyAccess: subscription.read_only_access || false,
        startDate: subscription.start_date ? subscription.start_date.split('T')[0] : '',
        endDate: subscription.end_date ? subscription.end_date.split('T')[0] : '',
        postSubscriptionReadOnlyDays: subscription.post_subscription_read_only_days || 0,
        billingCycleCharge: subscription.billing_cycle_charge || '',
        setupCharge: subscription.setup_charge || '',
        totalContractValue: subscription.total_contract_value || '',
        maxUsers: subscription.max_users || '',
        enforcedUserLimit: subscription.enforced_user_limit || false,
        autoRenew: subscription.auto_renew !== undefined ? subscription.auto_renew : true
      });
    } else {
      setEditingSubscription(null);
      setFormData({
        organizationId: '',
        offeringId: '',
        billingCycleId: '',
        currencyId: '',
        erpCode: '',
        paymentsCurrents: false,
        status: 'incomplete',
        readOnlyAccess: false,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        postSubscriptionReadOnlyDays: 30,
        billingCycleCharge: '',
        setupCharge: '',
        totalContractValue: '',
        maxUsers: '',
        enforcedUserLimit: false,
        autoRenew: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSubscription(null);
    setFormData({
      organizationId: '',
      offeringId: '',
      billingCycleId: '',
      currencyId: '',
      erpCode: '',
      paymentsCurrents: false,
      status: 'incomplete',
      readOnlyAccess: false,
      startDate: '',
      endDate: '',
      postSubscriptionReadOnlyDays: 30,
      billingCycleCharge: '',
      setupCharge: '',
      totalContractValue: '',
      maxUsers: '',
      enforcedUserLimit: false,
      autoRenew: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // If offering is selected, auto-populate related fields
    if (name === 'offeringId' && value) {
      const selectedOffering = offerings.find(offer => offer.id === parseInt(value));
      if (selectedOffering) {
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value,
          billingCycleId: selectedOffering.billing_cycle_id || '',
          currencyId: selectedOffering.currency_id || '',
          setupCharge: selectedOffering.setup_charge || '',
          billingCycleCharge: selectedOffering.billing_cycle_charge || ''
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError('');

    try {
      const subscriptionData = {
        organization_id: parseInt(formData.organizationId),
        offering_id: parseInt(formData.offeringId),
        billing_cycle_id: parseInt(formData.billingCycleId),
        currency_id: parseInt(formData.currencyId),
        erp_code: formData.erpCode?.trim() || null,
        payments_current: formData.paymentsCurrents,
        status: formData.status,
        read_only_access: formData.readOnlyAccess,
        start_date: formData.startDate,
        end_date: formData.endDate || null,
        post_subscription_read_only_days: parseInt(formData.postSubscriptionReadOnlyDays) || 0,
        billing_cycle_charge: parseFloat(formData.billingCycleCharge) || 0,
        setup_charge: parseFloat(formData.setupCharge) || 0,
        total_contract_value: parseFloat(formData.totalContractValue) || 0,
        max_users: parseInt(formData.maxUsers) || 0,
        enforced_user_limit: formData.enforcedUserLimit,
        auto_renew: formData.autoRenew
      };

      let response;
      if (editingSubscription) {
        response = await apiService.updateSubscription(editingSubscription.id, subscriptionData);
      } else {
        response = await apiService.createSubscription(subscriptionData);
      }

      if (response.success) {
        handleCloseModal();
        await loadSubscriptions();
      } else {
        setError(response.error || 'Failed to save subscription');
      }
    } catch (error) {
      console.error('Error saving subscription:', error);
      setError('Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'incomplete': 'warning',
      'active': 'success',
      'grace_period': 'info',
      'past_due': 'warning',
      'paused': 'secondary',
      'canceled': 'danger',
      'cancelled': 'danger',
      'renewed': 'primary',
      'expired': 'dark'
    };
    return variants[status] || 'secondary';
  };

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-credit-card me-2"></i>
              Subscriptions
            </h2>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Subscription
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Filter Section */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Filter by Status</Form.Label>
            <Form.Select>
              <option value="">All Statuses</option>
              <option value="incomplete">Incomplete</option>
              <option value="active">Active</option>
              <option value="grace_period">Grace Period</option>
              <option value="past_due">Past Due</option>
              <option value="paused">Paused</option>
              <option value="canceled">Canceled</option>
              <option value="renewed">Renewed</option>
              <option value="expired">Expired</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Items per page</Form.Label>
            <Form.Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value="10">10 per page</option>
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : subscriptions.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-credit-card display-1"></i>
                  <div className="mt-2">No subscriptions found</div>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Offering</th>
                      <th>ERP Code</th>
                      <th>Billing Cycle</th>
                      <th>Currency</th>
                      <th>Status</th>
                      <th>Payments Current</th>
                      <th>Read Only</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Setup Charge</th>
                      <th>Cycle Charge</th>
                      <th>Total Value</th>
                      <th>Max Users</th>
                      <th>Auto Renew</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((subscription) => (
                      <tr key={subscription.id}>
                        <td>
                          <div className="fw-medium">{subscription.organization_name}</div>
                          <small className="text-muted">{subscription.organization_code}</small>
                        </td>
                        <td>
                          <div className="fw-medium">{subscription.offering_name}</div>
                          <small className="text-muted">{subscription.offering_code}</small>
                        </td>
                        <td>{subscription.erp_code || <span className="text-muted">N/A</span>}</td>
                        <td>{subscription.billing_cycle_name || <span className="text-muted">N/A</span>}</td>
                        <td>{subscription.currency_code || <span className="text-muted">N/A</span>}</td>
                        <td>
                          <Badge bg={getStatusBadge(subscription.status)}>
                            {subscription.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={subscription.payments_current ? 'success' : 'danger'}>
                            {subscription.payments_current ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={subscription.read_only_access ? 'warning' : 'success'}>
                            {subscription.read_only_access ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                        <td>{apiService.formatDate(subscription.start_date)}</td>
                        <td>
                          {subscription.end_date
                            ? apiService.formatDate(subscription.end_date)
                            : <span className="text-muted">No end date</span>
                          }
                        </td>
                        <td>
                          {subscription.setup_charge
                            ? `${subscription.currency_code || ''} ${parseFloat(subscription.setup_charge).toFixed(2)}`
                            : <span className="text-muted">0.00</span>
                          }
                        </td>
                        <td>
                          {subscription.billing_cycle_charge
                            ? `${subscription.currency_code || ''} ${parseFloat(subscription.billing_cycle_charge).toFixed(2)}`
                            : <span className="text-muted">0.00</span>
                          }
                        </td>
                        <td>
                          {subscription.total_contract_value
                            ? `${subscription.currency_code || ''} ${parseFloat(subscription.total_contract_value).toFixed(2)}`
                            : <span className="text-muted">0.00</span>
                          }
                        </td>
                        <td>{subscription.max_users || 0}</td>
                        <td>
                          <Badge bg={subscription.auto_renew ? 'success' : 'secondary'}>
                            {subscription.auto_renew ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleShowModal(subscription)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card.Footer>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, subscriptions.length)} of {subscriptions.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingSubscription ? 'Edit Subscription' : 'Add Subscription'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {/* Basic Information Section */}
            <h6 className="mb-3 text-primary">
              <i className="bi bi-info-circle me-2"></i>
              Basic Information
            </h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Organization *</Form.Label>
                  <Form.Select
                    name="organizationId"
                    value={formData.organizationId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Organization</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name} ({org.code})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Offering *</Form.Label>
                  <Form.Select
                    name="offeringId"
                    value={formData.offeringId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Offering</option>
                    {offerings.map(offering => (
                      <option key={offering.id} value={offering.id}>
                        {offering.name} ({offering.code || offering.id})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Cycle</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      formData.billingCycleId
                        ? billingCycles.find(cycle => cycle.id === parseInt(formData.billingCycleId))?.name || 'Unknown'
                        : 'Select an offering first'
                    }
                    readOnly
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">
                    Determined by the selected offering
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      formData.currencyId
                        ? currencies.find(currency => currency.id === parseInt(formData.currencyId))?.name +
                        ` (${currencies.find(currency => currency.id === parseInt(formData.currencyId))?.code})` || 'Unknown'
                        : 'Select an offering first'
                    }
                    readOnly
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">
                    Determined by the selected offering
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ERP Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="erpCode"
                    value={formData.erpCode}
                    onChange={handleInputChange}
                    placeholder="Optional ERP reference code"
                  />
                  <Form.Text className="text-muted">
                    Optional code linking this subscription to the ERP system
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Status and Flags Section */}
            <h6 className="mb-3 mt-4 text-primary">
              <i className="bi bi-flag me-2"></i>
              Status & Configuration
            </h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.status === 'incomplete' ? 'Incomplete' :
                      formData.status === 'active' ? 'Active' :
                        formData.status === 'grace_period' ? 'Grace Period' :
                          formData.status === 'past_due' ? 'Past Due' :
                            formData.status === 'paused' ? 'Paused' :
                              formData.status === 'canceled' ? 'Canceled' :
                                formData.status === 'renewed' ? 'Renewed' :
                                  formData.status === 'expired' ? 'Expired' : 'Incomplete'}
                    readOnly
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">
                    {!editingSubscription ? 'New subscriptions start as Incomplete' : 'Status is managed by the system'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Users *</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxUsers"
                    value={formData.maxUsers}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="paymentsCurrents"
                    checked={formData.paymentsCurrents}
                    onChange={handleInputChange}
                    label="Payments Current"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="readOnlyAccess"
                    checked={formData.readOnlyAccess}
                    onChange={handleInputChange}
                    label="Read Only Access"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="enforcedUserLimit"
                    checked={formData.enforcedUserLimit}
                    onChange={handleInputChange}
                    label="Enforce User Limit"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="autoRenew"
                    checked={formData.autoRenew}
                    onChange={handleInputChange}
                    label="Auto Renew"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Post Subscription Read-Only Days</Form.Label>
                  <Form.Control
                    type="number"
                    name="postSubscriptionReadOnlyDays"
                    value={formData.postSubscriptionReadOnlyDays}
                    onChange={handleInputChange}
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Dates Section */}
            <h6 className="mb-3 mt-4 text-primary">
              <i className="bi bi-calendar me-2"></i>
              Subscription Period
            </h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Financial Information Section */}
            <h6 className="mb-3 mt-4 text-primary">
              <i className="bi bi-currency-dollar me-2"></i>
              Financial Information
            </h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Setup Charge *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="setupCharge"
                    value={formData.setupCharge}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Cycle Charge *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="billingCycleCharge"
                    value={formData.billingCycleCharge}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Total Contract Value *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="totalContractValue"
                    value={formData.totalContractValue}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Subscription'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Subscriptions;
